/**
 * AdminAgeGate — combined Support/Compliance surface for the talent
 * 18+ gate (iter 131).
 *
 * Two panels on one page:
 *   1. DOB correction — lookup a talent by email, review the current
 *      age_at_signup, and submit a corrected DOB with a required
 *      identity-verification note (support-tier + compliance-tier).
 *   2. Age-gate flag triage — list rows from the age_gate_flags
 *      collection (screening backstop + nightly policy-drift sweep)
 *      and resolve them with a note.
 *
 * Route: /admin/background-checks/age-flags — registered in
 * adminRoutes.jsx alongside the retention page.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Loader2, RefreshCw, ShieldAlert, ShieldCheck, User, Search,
    CheckCircle2, XCircle, PlayCircle, PencilLine, AlertTriangle, Download,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const StatusPill = ({ status }) => {
    const tone = status === "resolved" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
        : status === "under_review" ? "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400"
        : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400";
    return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${tone}`}>{status}</span>;
};

const DobCorrectionPanel = () => {
    const [emailQ, setEmailQ] = useState("");
    const [target, setTarget] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openCorrect, setOpenCorrect] = useState(false);
    const [correctedDob, setCorrectedDob] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const lookup = async () => {
        const q = emailQ.trim().toLowerCase();
        if (!q) return;
        setLoading(true); setTarget(null);
        try {
            // Compliance-scope lookup — returns just the shape the DOB
            // correction UI needs. Same RBAC as the correction endpoint.
            const { data } = await api.get("/admin/age-gate/user-lookup", { params: { email: q } });
            const u = data?.user || null;
            if (!u) {
                toast.error("No talent account found for that email.");
                setTarget(null);
            } else if (u.role !== "professional") {
                toast.error("That account is not a talent account. DOB correction is talent-only.");
                setTarget(null);
            } else {
                setTarget(u);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Lookup failed.");
        } finally {
            setLoading(false);
        }
    };

    const submit = async () => {
        if (!target) return;
        if (!correctedDob) { toast.error("Enter the corrected DOB."); return; }
        if (note.trim().length < 10) {
            toast.error("Identity verification note must describe what ID / evidence you reviewed (min 10 chars).");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.patch(`/admin/users/${target.user_id}/dob-correction`, {
                corrected_dob: correctedDob,
                identity_verification_note: note.trim(),
            });
            toast.success(`DOB corrected. New age_at_signup = ${data.new_age_at_signup}.`);
            setOpenCorrect(false);
            setCorrectedDob(""); setNote("");
            await lookup();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "DOB correction failed.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="card-soft p-5" data-testid="ag-dob-correction-panel">
            <div className="flex items-center gap-2 text-foreground">
                <PencilLine size={14} className="text-emerald-600" />
                <h2 className="font-heading font-semibold text-lg">DOB correction</h2>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
                Look up a talent by email, verify their identity out-of-band (e.g. government-ID photo emailed
                to <a href="mailto:support@simonaratalent.us" className="underline">support@simonaratalent.us</a>),
                then submit the corrected DOB with a note describing what evidence you reviewed. Every correction is audit-logged.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
                <Input
                    type="email"
                    value={emailQ}
                    onChange={(e) => setEmailQ(e.target.value)}
                    placeholder="talent@example.com"
                    data-testid="ag-dob-email-input"
                    className="max-w-sm"
                />
                <button
                    onClick={lookup}
                    disabled={loading || !emailQ.trim()}
                    className="btn-outline text-xs inline-flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="ag-dob-lookup-btn"
                >
                    {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Look up
                </button>
            </div>

            {target && (
                <div className="mt-4 rounded-lg border border-border p-4" data-testid="ag-dob-target">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="text-sm font-semibold text-foreground">{target.name || "(no name)"}</div>
                            <div className="text-xs text-muted-foreground">{target.email}</div>
                            <div className="mt-2 text-xs text-muted-foreground">
                                Age at signup: <strong className="text-foreground">{target.age_at_signup ?? "—"}</strong> ·
                                DOB attested: <strong className="text-foreground">{fmt(target.dob_attestation_at)}</strong>
                            </div>
                            {Array.isArray(target.dob_correction_history) && target.dob_correction_history.length > 0 && (
                                <div className="mt-2 text-[11px] text-muted-foreground italic">
                                    {target.dob_correction_history.length} prior correction(s) on record.
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => setOpenCorrect(true)}
                            className="btn-outline text-xs inline-flex items-center gap-1.5"
                            data-testid="ag-dob-open-correct-btn"
                        >
                            <PencilLine size={12} /> Correct DOB
                        </button>
                    </div>
                </div>
            )}

            <Dialog open={openCorrect} onOpenChange={setOpenCorrect}>
                <DialogContent data-testid="ag-dob-correct-dialog">
                    <DialogHeader>
                        <DialogTitle className="inline-flex items-center gap-2">
                            <PencilLine size={14} /> Correct DOB — {target?.email}
                        </DialogTitle>
                        <DialogDescription>
                            This writes a new encrypted DOB, updates <code>age_at_signup</code>, appends to
                            the correction history, and audit-logs the change. The user is not notified.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label htmlFor="ag-dob-corrected">Corrected DOB</Label>
                            <Input
                                id="ag-dob-corrected"
                                type="date"
                                value={correctedDob}
                                onChange={(e) => setCorrectedDob(e.target.value)}
                                max={new Date().toISOString().slice(0, 10)}
                                data-testid="ag-dob-corrected-input"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label htmlFor="ag-dob-note">Identity verification note <span className="text-rose-600">*</span></Label>
                            <Textarea
                                id="ag-dob-note"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="What ID / evidence did you review? (e.g. 'Verified via US driver's license photo emailed to support on 2026-03-01, DL#12345 matches user'). Min 10 chars."
                                rows={4}
                                data-testid="ag-dob-note-input"
                                className="mt-1"
                            />
                            <div className="mt-1 text-[10px] text-muted-foreground">
                                {note.length}/10+ characters
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <button onClick={() => setOpenCorrect(false)} className="btn-outline text-xs">Cancel</button>
                        <button
                            onClick={submit}
                            disabled={busy || !correctedDob || note.trim().length < 10}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="ag-dob-submit-btn"
                        >
                            {busy ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />} Confirm correction
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
};


const FlagTriagePanel = () => {
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState({});
    const [filter, setFilter] = useState("open");
    const [loading, setLoading] = useState(false);
    const [sweepBusy, setSweepBusy] = useState(false);
    const [resolveFor, setResolveFor] = useState(null);
    const [resolveNote, setResolveNote] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/background-checks/age-flags", {
                params: filter === "all" ? {} : { status: filter },
            });
            setRows(data?.results || []);
            setCounts(data?.status_counts || {});
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load age-gate flags.");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const runSweep = async () => {
        setSweepBusy(true);
        try {
            const { data } = await api.post("/admin/background-checks/age-flags/policy-drift-sweep");
            toast.success(`Sweep scanned ${data.scanned}, flagged ${data.flagged} (min age ${data.min_age}).`);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Sweep failed.");
        } finally { setSweepBusy(false); }
    };

    const patch = async (flag, status, note = "") => {
        try {
            await api.patch(`/admin/background-checks/age-flags/${flag.id}`, {
                status, resolution_note: note || undefined,
            });
            toast.success(`Flag ${flag.id.slice(0, 12)}… → ${status}.`);
            setResolveFor(null); setResolveNote("");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed.");
        }
    };

    return (
        <section className="card-soft p-5" data-testid="ag-flag-panel">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <div className="flex items-center gap-2 text-foreground">
                        <ShieldAlert size={14} className="text-amber-600" />
                        <h2 className="font-heading font-semibold text-lg">Age-gate flag triage</h2>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        Rows are written by the screening backstop (an employer tried to run a check on an
                        under-min talent) and the nightly policy-drift sweep (a policy bump left an existing
                        account below the new floor).
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button
                        onClick={() => {
                            const params = filter === "all" ? "" : `?status=${filter}`;
                            const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/background-checks/age-flags.csv${params}`;
                            const token = localStorage.getItem("simonara_token");
                            fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                                .then((r) => r.blob())
                                .then((blob) => {
                                    const dlUrl = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = dlUrl;
                                    a.download = `simonara-age-flags-${new Date().toISOString().slice(0,10)}.csv`;
                                    document.body.appendChild(a); a.click(); a.remove();
                                    URL.revokeObjectURL(dlUrl);
                                    toast.success("CSV downloaded.");
                                })
                                .catch(() => toast.error("CSV export failed."));
                        }}
                        className="btn-outline text-xs inline-flex items-center gap-1.5"
                        data-testid="ag-flag-csv-btn"
                    >
                        <Download size={12} /> Export CSV
                    </button>
                    <button onClick={runSweep} disabled={sweepBusy} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="ag-flag-sweep-btn">
                        {sweepBusy ? <Loader2 size={12} className="animate-spin" /> : <PlayCircle size={12} />} Run policy-drift sweep
                    </button>
                    <button onClick={load} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="ag-flag-refresh-btn">
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {["open", "under_review", "resolved", "all"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setFilter(s)}
                        className={`rounded-full border px-2.5 py-1 ${
                            filter === s ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-semibold" : "border-border text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid={`ag-flag-filter-${s}`}
                    >
                        {s} {counts[s] != null ? <span className="ml-1 opacity-70">({counts[s]})</span> : ""}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>
            ) : rows.length === 0 ? (
                <div className="mt-3 text-sm text-muted-foreground inline-flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-emerald-600" /> Nothing here — no {filter === "all" ? "" : filter} flags.
                </div>
            ) : (
                <div className="mt-3 space-y-2" data-testid="ag-flag-list">
                    {rows.map((r) => (
                        <div key={r.id} className="rounded-lg border border-border p-3" data-testid={`ag-flag-row-${r.id}`}>
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-[11px] text-muted-foreground">{r.id}</span>
                                        <StatusPill status={r.status || "open"} />
                                        <span className="text-[11px] text-muted-foreground">source: <code>{r.source}</code></span>
                                    </div>
                                    <div className="mt-1 text-sm text-foreground">
                                        {r.target_email} <span className="text-muted-foreground">·</span>{" "}
                                        {r.current_age != null ? <>current age <strong>{r.current_age}</strong> (below min <strong>{r.min_age}</strong>)</> : <>min age <strong>{r.min_age}</strong></>}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        Opened {fmt(r.at)} · Actor: {r.actor_email}
                                        {r.handled_by ? <> · Last handled by {r.handled_by} at {fmt(r.updated_at)}</> : null}
                                    </div>
                                    {r.resolution_note && (
                                        <p className="mt-1 text-[11px] text-muted-foreground italic">"{r.resolution_note}"</p>
                                    )}
                                </div>
                                {(r.status || "open") !== "resolved" && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {r.status !== "under_review" && (
                                            <button
                                                onClick={() => patch(r, "under_review")}
                                                className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400 px-2.5 py-1 text-[11px] font-semibold hover:bg-sky-500/15"
                                                data-testid={`ag-flag-review-${r.id}`}
                                            >
                                                <AlertTriangle size={11} /> Under review
                                            </button>
                                        )}
                                        <button
                                            onClick={() => { setResolveFor(r); setResolveNote(""); }}
                                            className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-2.5 py-1 text-[11px] font-semibold hover:bg-emerald-500/15"
                                            data-testid={`ag-flag-resolve-${r.id}`}
                                        >
                                            <CheckCircle2 size={11} /> Resolve
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!resolveFor} onOpenChange={(v) => !v && setResolveFor(null)}>
                <DialogContent data-testid="ag-flag-resolve-dialog">
                    <DialogHeader>
                        <DialogTitle>Resolve age-gate flag</DialogTitle>
                        <DialogDescription>Add a short note about how this was handled.</DialogDescription>
                    </DialogHeader>
                    <Textarea
                        value={resolveNote}
                        onChange={(e) => setResolveNote(e.target.value)}
                        placeholder="e.g. 'Verified DOB via corrected support ticket; account is 18+, sweep re-ran cleanly.'"
                        rows={4}
                        data-testid="ag-flag-resolve-note"
                    />
                    <DialogFooter className="gap-2">
                        <button onClick={() => setResolveFor(null)} className="btn-outline text-xs">Cancel</button>
                        <button
                            onClick={() => patch(resolveFor, "resolved", resolveNote)}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-semibold"
                            data-testid="ag-flag-resolve-confirm"
                        >
                            <CheckCircle2 size={11} /> Confirm resolve
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
};


export default function AdminAgeGate() {
    return (
        <div className="container-app py-10" data-testid="admin-age-gate-page">
            <header className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Compliance ops</p>
                    <h1 className="mt-2 font-heading font-semibold text-3xl tracking-tight text-foreground">
                        Age gate — corrections &amp; flags
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
                        Correct a mistyped DOB on a talent account (identity-verified), or triage flags
                        raised by the screening backstop and nightly policy-drift sweep.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link to="/admin/audit-log" className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="ag-audit-link">
                        <ShieldCheck size={12} /> Audit log
                    </Link>
                    <Link to="/admin/background-checks" className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="ag-back-link">
                        <ShieldCheck size={12} /> Back to background checks
                    </Link>
                </div>
            </header>
            <div className="mt-6 space-y-5">
                <DobCorrectionPanel />
                <FlagTriagePanel />
            </div>
        </div>
    );
}
